import React, { useEffect, useState, useRef, useCallback } from 'react';

import makeStyles from '@mui/styles/makeStyles';

import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';

import RecordIcon from './components/RecordIcon';
import StopIcon from './components/StopIcon';
import CameraIcon from './components/CameraIcon';
import BlockedIcon from './components/BlockedIcon';

import useMediaRecorder from './useMediaRecorder';

import debug from 'debug';
import Box from '@mui/material/Box';
const d = debug('videoStream');

interface VideoStreamProps {
  autoRequest?: boolean;
  mode?: 'dark' | 'light';
  onReceiveBlob?: (data: Blob) => void;
  maxLengthSeconds?: number;
}

const useStyles = makeStyles(
  {
    // style rule
    root: (props: VideoStreamProps) => ({
      position: 'relative',
      display: 'flex',
      height: '100vh',
    }),
    videoCapture: (props: VideoStreamProps) => ({
      flexGrow: 1,
      overflow: 'hidden',
      objectFit: 'contain',
      transform: 'scaleX(-1)',
    }),
    videoPreview: (props: VideoStreamProps) => ({
      flexGrow: 1,
      overflow: 'hidden',
      objectFit: 'contain',
    }),
    buttonConfirm: {
      position: 'absolute',
      bottom: 2,
      left: '50%',
      transform: 'translateX(-50%)',
      color: '#EEE',
    },
    bar: {
      // transition: 'none',
    },
  },
  { name: 'VideoStream' },
);

const progressUpdateInterval = 200;

const VideoStream: React.FC<VideoStreamProps> = (props: any) => {
  const { autoRequest = false, onReceiveBlob, maxLengthSeconds = 60 } = props;
  const classes = useStyles(props);
  const videoElement = useRef<HTMLVideoElement>(null);
  const [captureState, setCaptureState] = useState<'noaccess' | 'blocked' | 'pending' | 'recording' | 'previewing'>('noaccess');
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaStream, setMediaStream] = useState<MediaStream>();
  const { mediaRecorder, mediaRecorded } = useMediaRecorder(mediaStream);

  const constraints = {
    audio: true,
    video: {
      // width: 225 * 2,
      // height: 400 * 2,
    },
  };

  useEffect(() => {
    if (autoRequest) d('implementAutoRequest');
  }, [autoRequest]);

  useEffect(() => {
    if (mediaStream) {
      d('gotStream', mediaStream);
      return () => {
        if (mediaStream.active) {
          d('closingStream');
          mediaStream.getTracks().forEach((track) => track.stop());
        }
      };
    }
  }, [mediaStream]);

  const handleReceiveStream = (stream: any) => {
    setMediaStream(stream);
    d(stream);
    setCaptureState('pending');
  };

  const handleRequestAccess = () => {
    d('requesting Access');
    var supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
    d('suppertedConstraints', supportedConstraints);
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(handleReceiveStream)
      .catch((ev) => {
        setCaptureState('noaccess');
        d('stillNope', ev);
      });
  };

  const handleRecord = () => {
    setCaptureState('recording');
    setRecordingTime(0);
    try {
      if (mediaRecorder) mediaRecorder.start(1000); // 1000ms chunks...
    } catch (err) {
      console.debug('caught', err);
      setCaptureState('noaccess');
    }
  };

  const handleStop = useCallback(() => {
    setCaptureState('previewing');
    if (mediaRecorder) mediaRecorder.stop();
  }, [mediaRecorder]);

  const handleSave = () => {
    if (onReceiveBlob) onReceiveBlob(mediaRecorded);
  };

  useEffect(() => {
    if (videoElement && videoElement.current && mediaStream) {
      d('linkingStream', videoElement, mediaStream);
      //@ts-ignore
      videoElement.current.srcObject = mediaStream;
    }
  }, [videoElement, mediaStream]);

  // Handle max recording length...
  useEffect(() => {
    if (captureState === 'recording') {
      d('creatingTimeLimitTimeout');
      const timeout = setTimeout(() => {
        d('timelimitReached');
        handleStop();
      }, maxLengthSeconds * 1000);
      const interval = setInterval(() => {
        setRecordingTime((time) => time + progressUpdateInterval);
      }, progressUpdateInterval);
      return () => {
        clearTimeout(timeout);
        clearInterval(interval);
      };
    }
  }, [captureState, maxLengthSeconds, handleStop, setRecordingTime]);

  const percentageRecorded = Math.round((recordingTime / (maxLengthSeconds * 1000 - progressUpdateInterval)) * 100);

  return (
    <div className={classes.root}>
      <video className={classes.videoCapture} ref={videoElement} autoPlay playsInline muted style={{ display: captureState === 'previewing' ? 'none' : '' }} />
      {captureState === 'previewing' && mediaRecorded && (
        <video
          className={classes.videoPreview}
          src={window.URL.createObjectURL(mediaRecorded)}
          autoPlay
          playsInline
          onClick={(ev: any) => {
            ev.target.currentTime = 0;
            ev.target.play();
          }}
          // controls
        />
      )}
      {captureState === 'noaccess' && <CameraIcon onClick={handleRequestAccess} />}
      {captureState === 'blocked' && <BlockedIcon onClick={handleRequestAccess} />}
      {(captureState === 'pending' || captureState === 'previewing') && <RecordIcon onClick={handleRecord} />}
      {captureState === 'recording' && <StopIcon onClick={handleStop} />}
      {captureState === 'previewing' && (
        <div className={classes.buttonConfirm}>
          <Button size="large" onClick={handleSave} variant="outlined">
            Confirm &amp; Upload Video
          </Button>
        </div>
      )}
      {captureState === 'recording' && (
        <Box
          sx={{
            position: 'absolute',
            width: '100vw',
            bottom: 0,
          }}
        >
          <LinearProgress color="secondary" classes={{ bar: classes.bar }} variant="determinate" value={percentageRecorded} />
        </Box>
      )}
    </div>
  );
};

export default VideoStream;
